import store from './store';

import { formatDateFromConvert, getOperatorsCookie, getPartnersCookie, getDestinationSlugByName, fmtQueryParams } from './utils';
import { captureException } from './utils/sentry';

import { fetchPlans, fetchCategories } from './actions/searchActions';

import { fetchProductCoverage } from './actions/coverageActions';
import { fetchPurchaseStatus } from './actions/purchaseStatusActions';
import {
  sendingEmail,
  sendEmailFail,
  sendEmailDone,
  fetchComparisonProductsCoverage,
} from './actions/comparisonActions';

import {
  clearCoupon,
  fetchAddress,
  setCouponText,
  setPurchaseUuid,
  validatePurchase,
  fetchPurchaseData,
  fetchBenefitsList,
  fetchPaymentMethods,
  fetchDiscountCoupon,
  openModalPurchaseError,
  openModalPurchaseDateError,
  openModalGenericError,
  showInvalidCreditCardError,
  finishedPurchase,
  hideInvalidCreditCardError,
  openModalFailPayment,
  openModalCouponError,
} from './actions/purchaseActions';

// DJANGO
export const listBenefits = () => {
  const url = process.env.REACT_APP_GO_API_URL + '/benefits';
  const fetchOptions = {
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
    },
  };

  fetch(url, fetchOptions)
    .then((response) => response.json())
    .then((json) => store.dispatch(fetchBenefitsList(json)))
    .catch((error) => console.error(error));
};

// #region Comparison
export const comparePlans = ({ begin_date, end_date, destination, product_code }) => {
  const plans = product_code.replace(/%20/g, '+').split('+');

  function getComparationUrls(plan) {
    return `${process.env.REACT_APP_GO_API_URL}/product/destination/${destination}/${begin_date}/${end_date}/${plan}/info`;
  }

  const fetchOptions = {
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
    },
  };

  Promise.all(plans.map((plan) => fetch(getComparationUrls(plan), fetchOptions)))
    .then((response) => {
      Promise.all(
        response.map((data) => {
          if (data.status !== 200) return null;
          return data.json();
        })
      )
        .then((plansInfo) => {
          const newData = [];
          plansInfo.map((json) => {
            newData.push({
              id: json.id.toString(),
              name: json.name,
              min_age: json.price.child.min_age,
              max_age: json.price.elderly.max_age,
              cover_pregnant: json.cover_pregnancy,
              provider_code: json.provider_code,
              provider_name: json.provider.name,
              terms_url: json.term_url,
              covid_coverage: json.covid_coverage,
              travel_renewal: json.travel_renewal,
              receptive_product: json.receptive_product,
              description: json.description,
              benefits: json.benefits,
            });
          });
          const data = newData;

          store.dispatch(fetchComparisonProductsCoverage(data));
        })
        .catch();
    })
    .catch();
};
// #endregion

// #region Coverage
export const getProductCoverage = (params) => {
  if (
    !(
      params.hasOwnProperty('begin_date') &&
      params.hasOwnProperty('end_date') &&
      params.hasOwnProperty('destination') &&
      params.hasOwnProperty('product_code')
    )
  )
    return false;

  const url = `${process.env.REACT_APP_GO_API_URL}/product/destination/${params.destination}/${params.begin_date}/${params.end_date}/${params.product_code}/info`;

  const fetchOptions = {
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
    },
  };

  // TODO: add parcels to newJson
  return fetch(url, fetchOptions)
    .then((response) => response.json())
    .then((json) => {
      const newJson = {
        id: json.id,
        name: json.name,
        min_age: json.price.child.min_age,
        max_age: json.price.elderly.max_age,
        cover_pregnant: json.cover_pregnancy,
        provider_code: json.provider_code,
        provider_name: json.provider.name,
        terms_url: json.term_url,
        covid_coverage: json.covid_coverage,
        travel_renewal: json.travel_renewal,
        receptive_product: json.receptive_product,
        description: json.description,
        benefits: json.benefits,
      };
      const data = newJson;

      store.dispatch(fetchProductCoverage(data));
      return data;
    })
    .catch((error) => console.error(error));
};
// #endregion

// #region Search
export const getCategory = (params) => {
  const url = `${process.env.REACT_APP_GO_API_URL}/category`;

  const fetchOptions = {
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
    },
  };

  return fetch(url, fetchOptions)
    .then((response) => response.json())
    .then((json) => {
      const categories = json.categories.map(replacePropertyNames);
      store.dispatch(fetchCategories(categories, params));
      return json;
    })
    .catch((error) => console.error(error));
};

function replacePropertyNames(obj) {
  const newObj = {};

  for (const prop in obj) {
    switch (prop) {
      case 'categories':
        newObj.Categories = obj[prop].map(replacePropertyNames);
        break;
      case 'id':
        newObj.ID = obj[prop];
        break;
      case 'order_priority':
        newObj.OrderPriority = obj[prop];
        break;
      case 'name':
        newObj.Name = obj[prop];
        break;
      default:
        newObj[prop] = obj[prop];
        break;
    }
  }

  return newObj;
}

export const getProductQuotation = ({ begin_date, end_date, destination, product_code, context, benefits }) => {
  store.dispatch(fetchPlans([]));

  destination = destination.toLowerCase();

  let product_codes = [''];

  if (product_code) {
    product_codes = product_code.replace(/%20/g, '+').split('+');
  }

  const fetchOptions = {
    method: 'POST',
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
      'Content-Type': 'application/json',
    },
  };

  const request_body = {
    context,
    end_date,
    begin_date,
    destination,
    benefits: benefits || ['42', '51', '68', '69'],
  };

  const destinationSlug = getDestinationSlugByName(destination) || destination;

  Promise.all(
    product_codes.map((code) => {
      if (product_code) request_body.product_code = code;
      const options = {
        ...fetchOptions,
        body: JSON.stringify({
          ...request_body,
        }),
      };

      if (code) {
        return fetch(
          `${process.env.REACT_APP_GO_API_URL}/product/destination/${destinationSlug}/${begin_date}/${end_date}/${code}/info`,
          {
            ...fetchOptions,
            method: 'GET',
          }
        );
      }
      return fetch(
        `${process.env.REACT_APP_GO_API_URL}/product/destination/${destinationSlug}/${begin_date}/${end_date}/`,
        options
      );
    })
  )
    .then((response) => {
      Promise.all(response.map((data) => (data.status === 200 ? data.json() : {})))
        .then((json) => {
          if (!json[0] && !json.code) return;

          let plans;

          if (product_codes[0]) {
            [plans] = [json];
          } else if (json[0].length <= 1) {
            plans = json.map((plan) => plan[0]);
          } else {
            [plans] = json;
          }

          const newPlans = [];
          plans.forEach((plan) => {
            const categories = [];

            if (plan.categories !== undefined) {
              plan.categories.map((category) => {
                categories.push(replacePropertyNames(category));
              });
            }

            let newPlan = {};
            newPlan = {
              code: plan.code ? plan.code.toString() : plan.id,
              name: plan.name,
              provider_code: plan.provider_code.toString(),
              provider_name: plan.provider_name,
              child: {
                min_age: plan.price.child.min_age,
                max_age: plan.price.child.max_age,
                price: plan.price.child.price,
              },
              adult: {
                min_age: plan.price.adult.min_age,
                max_age: plan.price.adult.max_age,
                price: plan.price.adult.price,
              },
              elder: {
                min_age: plan.price.elderly.min_age,
                max_age: plan.price.elderly.max_age,
                price: plan.price.elderly.price,
              },
              extra: {
                special: plan.special_product,
                covid_coverage: plan.covid_coverage,
                travel_renewal: plan.travel_renewal,
                receptive_product: plan.receptive_product,
                specialist_indicator: plan.specialist_indicator,
                promotional_destination_product: plan.promotional_destination_product,
              },
              terms_url: plan.term_url,
              categories,
              benefits: [],
              upsell_product_id: plan.upsell_product_id,
            };

            if (plan.benefits[0].code) {
              newPlan.benefits = plan.benefits;
            } else {
              plan.benefits.map((item) =>
                newPlan.benefits.push({
                  code: item.benefits.id.toString(),
                  name: item.benefits.name,
                  coverage: item.coverage,
                })
              );
            }

            newPlans.push(newPlan);
          });

          plans = newPlans;

          store.dispatch(fetchPlans(plans, context));
        })
        .catch();
    })
    .catch();
};

export const fetchProductQuotation = ({begin_date, end_date, destination, product_code}) => {
  const fetchOptions = {
    method: 'POST',
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
      'Content-Type': 'application/json',
    },
  };

  const request_body = {
    begin_date,
    end_date,
    destination,
    greatfor: null,
    context: null,
    benefits: [],
    product_code,
    "partner": "",
    "provider": null
  };

  return fetch(
    `${process.env.REACT_APP_GO_API_URL}/quotation`,
    {...fetchOptions, body: JSON.stringify(request_body)}
  );
}


export const fetchUpsellPlan = async ({ begin_date, end_date, destination, product_code }) => {
  const destinationSlug = getDestinationSlugByName(destination.toLowerCase()) || destination.toLowerCase();

  const fetchOptions = {
    method: 'GET',
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
    },
  };

  const response = await fetch(
    `${process.env.REACT_APP_GO_API_URL}/product/destination/${destinationSlug}/${begin_date}/${end_date}/${product_code}/info`,
    fetchOptions
  );

  if (response.status !== 200) return null;

  const plan = await response.json();
  if (!plan) return null;

  const categories = [];
  if (plan.categories !== undefined) {
    plan.categories.forEach((category) => {
      categories.push(replacePropertyNames(category));
    });
  }

  const newPlan = {
    code: plan.code ? plan.code.toString() : plan.id,
    name: plan.name,
    provider_code: plan.provider_code.toString(),
    provider_name: plan.provider_name,
    child: {
      min_age: plan.price.child.min_age,
      max_age: plan.price.child.max_age,
      price: plan.price.child.price,
    },
    adult: {
      min_age: plan.price.adult.min_age,
      max_age: plan.price.adult.max_age,
      price: plan.price.adult.price,
    },
    elder: {
      min_age: plan.price.elderly.min_age,
      max_age: plan.price.elderly.max_age,
      price: plan.price.elderly.price,
    },
    extra: {
      special: plan.special_product,
      covid_coverage: plan.covid_coverage,
      travel_renewal: plan.travel_renewal,
      receptive_product: plan.receptive_product,
      specialist_indicator: plan.specialist_indicator,
    },
    terms_url: plan.term_url,
    categories,
    benefits: [],
    upsell_product_id: plan.upsell_product_id,
  };

  if (plan.benefits && plan.benefits.length > 0 && plan.benefits[0].code) {
    newPlan.benefits = plan.benefits;
  } else if (plan.benefits) {
    plan.benefits.forEach((item) =>
      newPlan.benefits.push({
        code: item.benefits.id.toString(),
        name: item.benefits.name,
        coverage: item.coverage,
      })
    );
  }

  return newPlan;
};

//DJANGO
export const getCouponData = (coupon, product_code, begin_date, end_date, destination) => {
  const isPurchasePage = () => window.location.pathname.indexOf('pedido') > -1;

  if (!coupon) {
    store.dispatch(clearCoupon());
    return;
  }

  if (coupon !== coupon.trim()) {
    store.dispatch(
      fetchDiscountCoupon({
        notFound: true,
      })
    );
    return;
  }

  store.dispatch(setCouponText(coupon));

  let url;

  if (isPurchasePage())
    url =
      process.env.REACT_APP_DJANGO_URL +
      process.env.REACT_APP_PATH +
      '/coupon' +
      '/?coupon_code=' +
      coupon.toUpperCase() +
      '&product_id=' +
      product_code +
      '&travel_coverage_begin=' +
      begin_date +
      '&travel_coverage_end=' +
      end_date +
      '&travel_destination=' +
      destination;
  else
    url = `${process.env.REACT_APP_DJANGO_URL}${process.env.REACT_APP_PATH
      }/pesquisa-cupom/${coupon.toUpperCase()}?begin-date=${begin_date}&end-date=${end_date}`;

  const fetchOptions = {
    headers: {
      Authorization: process.env.REACT_APP_DJANGO_AUTHORIZATION_URL,
    },
  };

  fetch(url, fetchOptions)
    .then((response) => {
      if (response.status === 200) {
        return response.json();
      }
      else if (response.status === 404)
        store.dispatch(
          fetchDiscountCoupon({
            notFound: true,
          })
        );
    })
    .then((json) => {
      if (json.status === 0 || json.status === '0') store.dispatch(fetchDiscountCoupon(json));
      else {
        store.dispatch(setCouponText(''));
        store.dispatch(
          fetchDiscountCoupon({
            notFound: true,
          })
        );
      }
    })
    .catch((error) => {
      store.dispatch(
        fetchDiscountCoupon({
          notFound: true,
        })
      );
    });
};

//DJANGO
export const getPaymentMethods = (params) => {
  const url = `${process.env.REACT_APP_DJANGO_URL}${process.env.REACT_APP_PATH}/pedido/api/payment-methods/`;
  const fetchOptions = {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Authorization: process.env.REACT_APP_DJANGO_AUTHORIZATION_URL,
    },
    body: JSON.stringify(params),
  };

  fetch(url, fetchOptions)
    .then((response) => response.json())
    .then((json) => store.dispatch(fetchPaymentMethods(json)))
    .catch((error) => console.error(error));
};
// #endregion

export const checkoutPurchase = (purchase_data) => {
  const fmtDate = (date) => {
    let spl_date = '';
    if (date.match(/^\d{2}\/\d{2}\/\d{4}/)) spl_date = date.split('/');
    return spl_date[2] + '-' + spl_date[1] + '-' + spl_date[0];
  };
  const fmtCPF = (cpf) => cpf.replace(/\.|\s|-/g, '');
  const fmtPassport = (passport) => passport.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

  const DT_CPF = 0;
  const DT_RG = 1;
  const DT_PASSPORT = 2;

  let holder = {};
  let parcels = 1;
  let safetypay = {};
  let creditcard = {};
  let selected_bank = [];
  let selected_brand = [];
  let selected_pm = purchase_data.paymentMethods.filter((pm) => pm.selected);
  selected_pm = selected_pm[0] || '';

  if (selected_pm.slug === 'credit-card') {
    holder = {
      full_name: purchase_data.creditCard.holder_name,
      cpf: fmtCPF(purchase_data.creditCard.holder_cpf),
    };

    parcels = purchase_data.creditCard.parcels.filter((parcel) => parcel.selected)[0].times;

    selected_brand = purchase_data.creditCard.brands.filter((brand) => brand.selected);
    selected_brand = selected_brand[0] || '';

    creditcard = {
      brand: selected_brand.brand_name,
      number: purchase_data.creditCard.number.replace(/\s/g, ''),
      cvv: purchase_data.creditCard.cvv,
      expiration_year: purchase_data.creditCard.expiry_year,
      expiration_month: purchase_data.creditCard.expiry_month,
    };
  } else if (selected_pm.slug === 'boleto') {
    holder = {
      full_name: purchase_data.boleto.name,
      cpf: fmtCPF(purchase_data.boleto.cpf),
    };
  } else if (selected_pm.slug === 'safetypay') {
    holder = {
      agency: purchase_data.safetypay.agency,
      full_name: purchase_data.safetypay.name,
      account: purchase_data.safetypay.account,
      cpf: fmtCPF(purchase_data.safetypay.cpf),
    };

    selected_bank = purchase_data.safetypay.banks.filter((bk) => bk.selected);
    selected_bank = selected_bank[0] || {};
    try {
      safetypay = {
        bank: selected_bank.slug,
        bank_branch: purchase_data.safetypay.agency,
        bank_account: purchase_data.safetypay.account || '',
        cpf: purchase_data.safetypay.cpf,
      };
    } catch (err) {
      safetypay = {
        bank: '',
        bank_branch: '',
        bank_account: '',
      };
    }
  } else if (selected_pm.slug === 'cash') {
    holder = {
      full_name: purchase_data.cash.holderName,
      cpf: fmtCPF(purchase_data.cash.holderCpf),
    };
  } else if (selected_pm.slug === 'pix') {
    holder = {
      full_name: purchase_data.pix.name,
      cpf: fmtCPF(purchase_data.pix.cpf),
    };
  }

  let paymentMethod = selected_pm.id.toString();

  const { tag } = fmtQueryParams(purchase_data.quotationParams.queryParams);

  let creditCouponCode = null;
  if (purchase_data.creditCoupon.code && purchase_data.creditCoupon.applied) {
    creditCouponCode = purchase_data.creditCoupon.code;
  }

  if (purchase_data.purchaseSummary.total === 0) {
    paymentMethod = null;
  }

  const fmtPurchaseData = {
    departure_date: purchase_data.quotationParams.departure_date,
    return_date: purchase_data.quotationParams.return_date,
    product_code: purchase_data.plans[0].code.toString(),
    destination: purchase_data.quotationParams.destination,
    payment_method_discount: purchase_data.purchaseSummary.paymentMethodDiscountPercent,
    payment_method_discount_value: parseFloat(purchase_data.purchaseSummary.paymentMethodDiscountValue),
    tags: tag,
    newflux: false,
    insureds: purchase_data.insuredForms.map((insured) => ({
      full_name: insured.name,
      document_type:
        insured.document_type === 'cpf'
          ? DT_CPF
          : insured.document_type === 'rg'
            ? DT_RG
            : insured.document_type === 'passport'
              ? DT_PASSPORT
              : '',
      document:
        insured.document_type === 'cpf'
          ? fmtCPF(insured.cpf)
          : insured.document_type === 'rg'
            ? insured.rg
            : insured.document_type === 'passport'
              ? fmtPassport(insured.passport)
              : '',
      birth_date: fmtDate(insured.birth_date),
      origin_destination: insured.incoming,
    })),
    cash_allowed_by: {
      username: purchase_data.cash.username,
      password: purchase_data.cash.password,
    },
    holder,
    payment: {
      payment_method: paymentMethod,
      parcels,
      credit_coupon_code: creditCouponCode,
      coupon_code:
        purchase_data.coupon &&
        purchase_data.coupon.exists &&
        purchase_data.coupon.text &&
        purchase_data.coupon.text.toUpperCase(),
    },
    safetypay,
    creditcard,
    contact: {
      phone: purchase_data.contact.phone_number.replace(/\(|\)|\s|-/g, ''),
      contact_name: purchase_data.contact.contact_name,
      email: purchase_data.contact.email,
    },
    address: {
      address_cep: purchase_data.contact.cep.replace(/\.|\(|\)|\s|-/g, ''),
      address: purchase_data.contact.address,
      address_number: (!purchase_data.contact.number || purchase_data.contact.number === '') ? '1' : purchase_data.contact.number,
      address_complement: purchase_data.contact.complement,
      address_district: purchase_data.contact.district,
      address_state: purchase_data.contact.state,
      address_city: purchase_data.contact.city,
    },
    b2b: {
      partner: purchase_data.partner,
      partner_slug: purchase_data.partner,
      seller_slug: null,
      seller: null,
    },
    extra: {
      displayedPrice: purchase_data.purchaseSummary.total,
      dateTimeTermsAccept: purchase_data.dateTimeAccept,
      coupon_discount: purchase_data.purchaseSummary.coupon_discount,
      no_discount: purchase_data.purchaseSummary.subtotal,
      device: document.documentElement.clientWidth <= 769 ? 'mobile' : 'desktop',
    },
    operators: getOperatorsCookie(),
    partners: getPartnersCookie(),
    ...(purchase_data.hasConfirmedOperator && { user_accepted_operator_confirmation_modal: true }),
  };

  const url = `${process.env.REACT_APP_GO_API_URL}/purchase`;

  const fetchOptions = {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(fmtPurchaseData),
  };

  return fetch(url, fetchOptions)
    .then((response) => {
      store.dispatch(finishedPurchase());
      if (response.status === 200 || response.status === 202) return response.json();
      else if (response.status === 400 || response.status === 500)
        response.json().then((json) => {
          store.dispatch(validatePurchase(-1, 'fail', json.message));
          if (json.message === 'Cartão inválido') {
            store.dispatch(showInvalidCreditCardError());
            window.scrollTo(0, 0);
          } else if (json.type === "d0") {
            store.dispatch(openModalPurchaseDateError());
          }
          else if (json.error === "duplicated purchase") {
            store.dispatch(openModalPurchaseError());
          }
          else if (json.error === "coupon not available") {
            store.dispatch(openModalCouponError());
          } else {
            captureException(new Error('Erro inesperado na compra'), {
              url,
              status: response.status,
              response: json,
            });
            store.dispatch(openModalGenericError())
            store.dispatch(hideInvalidCreditCardError());
          }
        });
    })
    .then((json) => {
      if (json.reproved === true) {
        store.dispatch(openModalFailPayment());
        return;
      }
      if (json.uuid) {
        store.dispatch(setPurchaseUuid(json.uuid));
        store.dispatch(validatePurchase(1, 'success'));
      } else store.dispatch(validatePurchase(-1, 'fail', json.message));
    })
    .catch((error) => {
      store.dispatch(validatePurchase(-1, 'fail'));
    });
};

export const getPurchaseStatus = (uuid) => {
  const url = `${process.env.REACT_APP_GO_API_URL}/purchase/status/${uuid}`;

  const fetchOptions = {
    method: 'GET',
    headers: {
      Authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
    },
  };

  fetch(url, fetchOptions)
    .then((response) => response.json())
    .then((json) => {
      let purchase_status = {
        actionpay_pixel_url: json.status.pixels.actionpay_pixel_url,
        meliuz_pixel_url: json.status.pixels.meliuz_pixel_url,
        cityads_pixel_url: json.status.pixels.cityads_pixel_url,
        id: json.status.id,
        status: json.status.status,
        payment_method: json.status.payment_method,
        departure_date: json.status.departure_date,
        return_date: json.status.return_date,
        parcels: json.status.parcels,
        price: json.status.price,
        product: json.status.product,
        products_id: json.status.product_id,
        provider: json.status.provider,
        destination: json.status.destination,
        contact_name: json.status.contact_name,
        coupon_code: json.status.coupon_code,
        payment: json.status.payment,
        payment_status: json.status.payment_status,
        insureds: json.status.insureds,
        created_at: json.status.created_at,
      };

      if (json.status.credit_coupon) {
        purchase_status.credit_coupon = json.status.credit_coupon;
      }

      if (json.status.payment_method === 'Cartão de Crédito') {
        purchase_status.payment = {
          ...purchase_status.payment,
          brand: json.status.payment.creditcard_brand,
          number: json.status.payment.creditcard_number,
        };
      } else if (json.status.payment_method === 'Boleto') {
        purchase_status.status = purchase_status.status === 'Pendente Boleto' ? 'Pendente' : purchase_status.status;
        purchase_status.payment = {
          ...purchase_status.payment,
          deadline: json.status.payment.dead_line.substring(0, 10),
          barcode: json.status.payment.bar_code,
        };
      } else if (json.status.payment_method === 'Transferência Bancária') {
        purchase_status.status =
          purchase_status.status === 'Pendente Transferência' ? 'Pendente' : purchase_status.status;
        purchase_status.payment = {
          ...purchase_status.payment,
          active_safetypay: {
            deadline: formatDateFromConvert(json.status.payment.dead_line),
            extra: json.status.payment.extra,
            holder_bank: json.status.payment.holder_bank.replaceAll('-', ' '),
            holder_cpf: json.status.payment.holder_cpf,
            redirect_url: json.status.payment.redirect_url,
            transaction_id: json.status.payment.transaction_id,
          },
          bank_img_path: json.status.payment.bank_img_path.replace('segurospromo.com.br', ''),
          bank_name: json.status.payment.bank_name.replaceAll('-', ' '),
        };
      } else if (json.status.payment_method === 'PIX') {
        purchase_status.payment = {
          ...purchase_status.payment,
          deadline: formatDateFromConvert(json.status.payment.dead_line),
        };
      }

      store.dispatch(fetchPurchaseStatus(purchase_status));
    })
    .catch((error) => console.error(error));
};

export const sendEmail = (pageParams, type, toAddress) => {
  const { product_code, destination, begin_date, end_date, products_ids, coupon } = pageParams;

  store.dispatch(sendingEmail());

  const form = new FormData();
  form.append('to_email', toAddress);

  // TODO (Igor): remover quando for pra produção 100%
  let url = window.location.toString();

  form.append('url', url);

  if (type === 'detail') {
    url = `${process.env.REACT_APP_DJANGO_URL}${process.env.REACT_APP_PATH}/product-detail-email/${product_code}/${destination}/${begin_date}/${end_date}/`;
  } else if (type === 'comparison') {
    url = `${process.env.REACT_APP_DJANGO_URL}${process.env.REACT_APP_PATH}/product-comparison-email/${destination}/${begin_date}/${end_date}/`;
    form.append('products_ids', products_ids.join(' '));
  }

  url += '?no-redirect=no-redirect';

  if (coupon && coupon !== '') url += `&cupom=${coupon}`;

  fetch(url, {
    body: form,
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Authorization: process.env.REACT_APP_DJANGO_AUTHORIZATION_URL,
    },
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.success) {
        store.dispatch(sendEmailDone());
      } else {
        store.dispatch(sendEmailFail());
      }
    })
    .catch(() => {
      store.dispatch(sendEmailFail());
    });
};

export const getAddressData = (cep) => {
  if (cep.length < 8) return;
  if (typeof cep !== 'string') return;

  const url = `https://amocep.amopromo.com/api/v1/cep/${cep.replace(/\.|\s|-/g, '')}`;
  const fetchOptions = {
    method: 'GET',
  };
  fetch(url, fetchOptions).then((response) => {
    if (response.status === 200)
      return response.json().then((json) => {
        store.dispatch(
          fetchAddress({
            tipo_logradouro: json.tipo_logradouro || '',
            bairro: json.neighborhood || '',
            cidade: json.city || '',
            uf: json.state || '',
            logradouro: json.street || '',
          })
        );
      });
    store.dispatch(
      fetchAddress({
        tipo_logradouro: '',
        bairro: '',
        cidade: '',
        uf: '',
        logradouro: '',
      })
    );
  });
};

export const getPurchaseData = (data) => {
  const url = `${process.env.REACT_APP_DJANGO_URL}${process.env.REACT_APP_PATH}/pedido/api/purchase-data/${data}/`;

  fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.status !== 200) return null;

    return response.json().then((json) => {
      store.dispatch(fetchPurchaseData(json));
    });
  });
};

// Chip
// export const getChipUrl = async (uuid) => {
//   const url = `${process.env.REACT_APP_CHIP_URL}/purchase/cross-sell/${uuid}`;

//   const options = {
//     method: 'GET',
//     headers: {
//       'Content-Type': 'application/json',
//       Authorization: `Bearer ${process.env.REACT_APP_CHIP_BEARER}`,
//     },
//   };

//   const dataValues = await fetch(url, options)
//     .then((response) => {
//       if (response.status !== 200) return null;

//       return response.json();
//     })
//     .then((data) => data);

//   return dataValues;
// };

export const fetchEcommerceConfigService = () => {
  const url = `${process.env.REACT_APP_API_BASE_URL_DJANGO}/config/ecommerce-config/`;

  const response = fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.status !== 200) return null;

    return response.json();
  });

  return response;
};

export const fetchProvidersService = () => {
  const url = `${process.env.REACT_APP_API_BASE_URL_DJANGO}/config/providers/`;

  const response = fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.status !== 200) return null;

    return response.json();
  });

  return response;
};

export const fetchSortOptionsService = () => {
  const url = `${process.env.REACT_APP_API_BASE_URL_DJANGO}/config/sort-options/`;

  const response = fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.status !== 200) return null;

    return response.json();
  });

  return response;
};

export const fetchDestinationsService = () => {
  const url = `${process.env.REACT_APP_API_BASE_URL_DJANGO}/config/destinations/`;

  const response = fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.status !== 200) return null;

    return response.json();
  });

  return response;
};

export const fetchPaymentMethodsService = () => {
  const url = `${process.env.REACT_APP_API_BASE_URL_DJANGO}/config/payment-methods/`;

  const response = fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.status !== 200) return null;

    return response.json();
  });

  return response;
};

export const fetchHighlightPlans = (match) => {
  const { destination, begin_date, end_date } = match;
  const url = `${process.env.REACT_APP_GO_API_URL}/product/destination/${destination}/${begin_date}/${end_date}/highlight`;

  const response = fetch(url, {
    headers: {
      authorization: process.env.REACT_APP_GO_AUTHORIZATION_URL,
    },
  }).then((response) => {
    if (response.status !== 200) return null;

    return response.json();
  });

  return response;
};



// WEBPACK FOOTER //
// ./src/apiCalls.js